import { Injectable, computed, inject, signal } from '@angular/core';
import { last, uniq } from 'lodash-es';
import { distinctUntilChanged, skip } from 'rxjs';
import { LayoutEntryWithoutSlot } from './chart-configuration-provider';
import { DataSet, Signal, SignalNoData } from './data-provider';
import { GlobalSettingsProvider } from './global-settings-provider';
import { SignalSettingsProvider, StyleMap } from './signal-settings-provider';
import { Slot } from '../hgm-setup/src/module/param/core/slot';
import { arrayLikeEqual, arrayMax, arrayMin } from '../hgm-setup/src/module/util/core';

export interface UserSignalRange {
    dedicatedY?: boolean;
    minY?: number;
    maxY?: number;
}

export interface SignalLayoutEntry {
    key: string;
    type: 'signal';
    name: string;
    slot: Slot;
}

export interface DividerLayoutEntry {
    key: string;
    type: 'divider';
}

export type LayoutEntry = SignalLayoutEntry | DividerLayoutEntry;

export type GroupSignal = Signal & { minY: number; maxY: number };

@Injectable({ providedIn: 'root' })
export class SignalGroupProvider {
    readonly styledGroups = computed(() => this.styledGroupsSignal());
    readonly styledGroupsNoData = computed(() => this.styledGroupsNoDataSignal());
    readonly appliedConfigurationFileName = computed(() => this.configurationFileNameSignal());

    private dataSignalKeys: string[] = [];
    private readonly layoutEntriesSignal = signal<LayoutEntry[]>([]);
    private readonly layoutEntriesWithoutSlotSignal = signal<LayoutEntryWithoutSlot[]>([]);
    private readonly styledGroupsSignal = signal<{ groups: GroupSignal[][]; styles: StyleMap } | undefined>(undefined);
    private readonly styledGroupsNoDataSignal = signal<{ groups: SignalNoData[][]; styles: StyleMap } | undefined>(undefined);
    private readonly configurationFileNameSignal = signal<string | undefined>(undefined);
    private readonly signalSetter = inject(SignalSettingsProvider);
    private readonly globalSettings = inject(GlobalSettingsProvider);

    constructor() {
        this.signalSetter.styledDataObs.subscribe({ next: this.onStyledData });
        this.globalSettings.chartDivPolicyObs.pipe(distinctUntilChanged(), skip(1)).subscribe({ next: this.reset });
    }

    get layout(): LayoutEntry[] {
        return this.layoutEntriesSignal();
    }

    set layout(entries) {
        if (!arrayLikeEqual(entries, this.layoutEntriesSignal())) {
            this.layoutEntriesSignal.set(entries);
            this.updateGroups();
        }
    }

    get layoutNoSlot(): LayoutEntryWithoutSlot[] {
        return this.layoutEntriesWithoutSlotSignal();
    }

    set layoutNoSlot(entries) {
        if (!arrayLikeEqual(entries, this.layoutEntriesWithoutSlotSignal())) {
            this.layoutEntriesWithoutSlotSignal.set(entries);
            this.updateGroupsNoData();
        }
    }

    setAppliedConfigurationFileName(value: string | undefined) {
        this.configurationFileNameSignal.set(value);
    }

    reset = () => {
        this.dataSignalKeys = [];
        this.onStyledData(this.signalSetter.styledData);
    };

    updateGroups() {
        const { data, styles } = this.signalSetter.styledData || {
            data: undefined,
            styles: {},
        };
        if (!data) {
            this.styledGroupsSignal.set(undefined);
            return;
        }

        const groups: GroupSignal[][] = [[]];
        for (const entry of this.layoutEntriesSignal()) {
            if (entry.type === 'signal') {
                const signal = data.signals[entry.key];
                const yValues = signal.data.map((d) => d[1]).filter((y) => isFinite(y));
                last(groups)!.push({
                    ...signal,
                    minY: arrayMin(yValues),
                    maxY: arrayMax(yValues),
                });
            } else {
                console.assert(entry.type === 'divider');
                groups.push([]);
            }
        }

        this.styledGroupsSignal.set({ groups, styles });
    }

    updateGroupsNoData() {
        const { data, styles } = this.signalSetter.styledData || {
            data: undefined,
            styles: {},
        };
        if (!data) {
            this.styledGroupsNoDataSignal.set(undefined);
            return;
        }

        const groups: SignalNoData[][] = [[]];
        for (const entry of this.layoutEntriesWithoutSlotSignal()) {
            if (entry.type === 'signal') {
                const signalNoData = {
                    key: entry.key,
                    name: entry.name,
                    unit: '',
                    data: [],
                };
                last(groups)!.push(signalNoData);
            } else {
                console.assert(entry.type === 'divider');
                groups.push([]);
            }
        }

        this.styledGroupsNoDataSignal.set({ groups, styles });
    }

    private onStyledData = (styledData: { data: DataSet; styles: StyleMap } | undefined) => {
        if (!styledData) {
            this.layoutEntriesSignal.set([]);
            this.styledGroupsSignal.set(undefined);
            this.styledGroupsNoDataSignal.set(undefined);
            return;
        }

        const signalKeys = Object.keys(styledData.data.signals).sort();
        const signalsChanged = !arrayLikeEqual(signalKeys, this.dataSignalKeys);
        this.dataSignalKeys = signalKeys;

        if (signalsChanged) {
            const newEntries: LayoutEntry[] = [];
            switch (this.globalSettings.chartDivPolicy()) {
                case 'single':
                    for (const key of signalKeys) {
                        newEntries.push({
                            key,
                            type: 'signal',
                            name: styledData.data.signals[key].name,
                            slot: styledData.data.signals[key].slot,
                        });
                    }
                    break;
                case 'perSlot': {
                    const slots = uniq(Object.values(styledData.data.signals).map((s) => s.slot)).sort((a, b) => {
                        const unitA = a?.unit || '';
                        const unitB = b?.unit || '';
                        return unitA.localeCompare(unitB);
                    });
                    for (const [i, slot] of slots.entries()) {
                        const signalEntries = Object.entries(styledData.data.signals)
                            .filter((e) => e[1].slot === slot)
                            .sort();
                        for (const signalEntry of signalEntries) {
                            newEntries.push({
                                key: signalEntry[0],
                                type: 'signal',
                                name: signalEntry[1].name,
                                slot: styledData.data.signals[signalEntry[0]].slot,
                            });
                        }
                        if (i < slots.length - 1) {
                            newEntries.push({
                                key: 'divider' + i,
                                type: 'divider',
                            });
                        }
                    }
                    break;
                }
                case 'perSignal':
                    for (const [i, key] of signalKeys.entries()) {
                        newEntries.push({
                            key,
                            type: 'signal',
                            name: styledData.data.signals[key].name,
                            slot: styledData.data.signals[key].slot,
                        });
                        if (i < signalKeys.length - 1) {
                            newEntries.push({
                                key: 'divider' + i,
                                type: 'divider',
                            });
                        }
                    }
                    break;
            }
            this.layoutEntriesSignal.set(newEntries);
        }

        this.updateGroups();
    };
}
